<script lang="ts">
  import { untrack } from 'svelte';
  import type { HudState } from './hud-state.svelte';
  import ScrapBar from './hud/ScrapBar.svelte';
  import BaseHealth from './hud/BaseHealth.svelte';
  import Leaderboard from './hud/Leaderboard.svelte';
  import UpgradeDock from './hud/UpgradeDock.svelte';
  import AbilityPanel from './hud/AbilityPanel.svelte';
  import Minimap from './hud/Minimap.svelte';
  import EvolveScreen from './hud/EvolveScreen.svelte';
  import Chat from './hud/Chat.svelte';
  import EventTimer from './hud/EventTimer.svelte';
  import EndGameScreen from './hud/EndGameScreen.svelte';
  import SurrenderPanel from './hud/SurrenderPanel.svelte';
  import ChatComposer from './hud/ChatComposer.svelte';
  import SpectatorBar from './hud/SpectatorBar.svelte';
  import DeathOverlay from './hud/DeathOverlay.svelte';
  import TeamStatsPanel from './hud/TeamStatsPanel.svelte';
  import { socketState } from './socket-state.svelte';
  import type { World } from '../game/world';
  import type { AssetStore } from '../assets/asset-store';
  import type { SettingsStore } from '../settings/settings-store';
  import { settingsOverlay } from './settings-open.svelte';
  import { MINIMAP } from '../game/constants/hud';

  interface Props {
    hud: HudState;
    /** The minimap renders world coordinates directly; everything else reads the snapshot. */
    world: World;
    assets: AssetStore;
    settings: SettingsStore;
    onbuy: (slot: number) => void;
    /** Raises the `evolve` intent for a chosen `EvolveShip.path`. */
    onevolve: (path: number) => void;
    onsurrender: (agree: boolean) => void;
    onchat: (text: string) => void;
    /** Spectators only: the seat-less exit, which the lobby screen used to own. */
    onleave?: (() => void) | undefined;
    onseek?: ((x: number, y: number) => void) | undefined;
  }

  const {
    hud,
    world,
    assets,
    settings,
    onbuy,
    onevolve,
    onsurrender,
    onchat,
    onleave,
    onseek,
  }: Props = $props();

  let showFps = $state(untrack(() => settings.get('showFps')));

  $effect(() =>
    settings.subscribe(() => {
      showFps = settings.get('showFps');
    }),
  );

  /** Clear of the minimap's frame art, which overhangs the map by MINIMAP.frameInset.y. */
  const settingsBottom = -MINIMAP.position.yFromBottom - MINIMAP.frameInset.y + 8;
</script>

<!--
  The HUD overlay.

  Sized to the UI-space viewport and zoomed onto the canvas, so every child can use the pixel
  offsets the game shipped with — see `constants/hud.ts`. The zoom is `uiScale / devicePixelRatio`
  because the canvas is in device pixels and this is in CSS pixels.

  `zoom`, not `transform: scale()`: the zoom eases every frame around death, and a transform would
  re-rasterize the layer at a new size and scale each frame, quantizing text and art edges
  differently frame to frame — the jitter this page reported. `zoom` bakes the scale into layout, so
  the layer repaints at 1:1 like the original's canvas HUD.

  `pointer-events: none` on the root and `auto` on the widgets that want clicks: without that, an
  invisible full-screen div would swallow every shot the player fires.
-->
<div
  class="hud"
  style:width="{hud.uiWidth}px"
  style:height="{hud.uiHeight}px"
  style:zoom={hud.cssScale}
>
  {#if hud.playing && !hud.dead && !hud.spectating}
    <ScrapBar scrap={hud.scrap} />
    <AbilityPanel {hud} />
    <UpgradeDock slots={hud.dock} {onbuy} />
  {/if}

  {#if hud.spectating && !hud.lobbyOpen}
    <SpectatorBar
      host={socketState.lobby?.hostName ?? ''}
      following={hud.following}
      followingName={hud.followingName}
      viewWidth={hud.viewWidth}
      onleave={() => onleave?.()}
    />
    {#if hud.teamStats !== null}
      <TeamStatsPanel
        view={hud.teamStats}
        open={hud.teamStatsOpen}
        ontoggle={() => {
          hud.teamStatsOpen = !hud.teamStatsOpen;
        }}
      />
    {/if}
  {/if}

  <!-- Match furniture. Gated, or a minimap and a base-health bar float behind the front screen. -->
  {#if hud.playing || hud.dead}
    <!-- The lobby screen draws its own log and composer over this one; two at once is one too many. -->
    {#if !hud.lobbyOpen}
      <Chat lines={hud.chat} uiWidth={hud.uiWidth} uiHeight={hud.uiHeight} />
      <!-- A started spectator holds no seat, so the server refuses the line; offering the field invites the refusal. -->
      {#if hud.chatEnabled && !hud.spectating}
        <ChatComposer mode="hud" onsend={onchat} />
      {/if}
    {/if}
    <EventTimer label={hud.eventLabel} countdown={hud.eventCountdown} />

    <Minimap {world} {assets} now={hud.now} onseek={hud.spectating ? onseek : undefined} />
    <button
      class="settings-button"
      type="button"
      aria-label="Settings"
      data-testid="hud-settings"
      style:bottom="{settingsBottom}px"
      onclick={() => {
        settingsOverlay.show();
      }}
    >
      <i class="wis-icon wis-icon--gear"></i>
    </button>
    <BaseHealth blue={hud.blueBaseHealth} red={hud.redBaseHealth} max={hud.baseMaxHealth} />
    <Leaderboard rows={hud.leaderboard} rank={hud.myRank} />
    {#if hud.surrender !== null && hud.endGame === null && !hud.spectating}
      <SurrenderPanel view={hud.surrender} onvote={onsurrender} />
    {/if}
  {/if}

  {#if showFps}
    <p class="fps" data-testid="hud-fps">{hud.fps} FPS</p>
  {/if}

  {#if hud.countdownSeconds > 0}
    <div class="countdown" data-testid="hud-countdown">
      <span class="countdown__number">{hud.countdownSeconds}</span>
      <span class="countdown__label">Get ready</span>
    </div>
  {/if}

  {#if hud.notification !== '' && !socketState.shellVisible}
    <p class="notification">{hud.notification}</p>
  {/if}

  {#if hud.evolveScreenOpen}
    <EvolveScreen
      shipId={hud.selfShipId}
      team={hud.selfTeam}
      evolutionAbilities={hud.evolutionAbilities}
      onchoose={(path) => {
        onevolve(path);
        hud.evolveScreenOpen = false;
      }}
      onclose={() => {
        hud.evolveScreenOpen = false;
      }}
    />
  {/if}

  {#if hud.endGame !== null}
    <EndGameScreen info={hud.endGame} selfTeam={hud.selfTeam} />
  {/if}

  {#if hud.dead && !hud.spectating}
    <DeathOverlay
      respawnSeconds={hud.respawnSeconds}
      returnToMenuRequested={hud.returnToMenuRequested}
      tip={hud.deathTip}
    />
  {/if}
</div>

<style>
  .settings-button {
    position: absolute;
    left: 10px;
    width: 30px;
    height: 30px;
    padding: 0;
    background: none;
    border: none;
    opacity: 0.55;
    cursor: pointer;
    pointer-events: auto;
  }

  .settings-button:hover,
  .settings-button:focus-visible {
    opacity: 1;
  }

  /* Below the scrap plate, which is 82px tall and the only thing in this corner. */
  .fps {
    position: absolute;
    top: 88px;
    left: 12px;
    margin: 0;
    color: #6f6;
    font-size: 12px;
    text-shadow: 0 1px 2px #000;
  }

  .hud {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    font-family: xirod, sans-serif;
    user-select: none;
    -webkit-user-select: none;
  }

  /* Centred at y=75, 20px, held for fifteen seconds. */
  .countdown {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    pointer-events: none;
    text-shadow: 0 3px 8px #000;
  }

  .countdown__number {
    font-size: 108px;
    line-height: 1;
    color: #fff;
  }

  .countdown__label {
    font-size: 16px;
    text-transform: uppercase;
    letter-spacing: 6px;
    color: #cfd6e6;
  }

  .notification {
    position: absolute;
    top: 60px;
    left: 0;
    width: 100%;
    margin: 0;
    text-align: center;
    font-size: 20px;
    color: #ee1111;
  }
</style>
